import { BaseSqliteRepository } from './base-sqlite.repository';
import { SqliteQueryError } from '../../errors';
import type { ICurrencyRateRepository } from '@app/domain/ports';
import type { RateFilter, RateCursor } from '@app/domain/ports';
import type { CurrencyRate } from '@app/domain/entities';

interface RateRow {
  id: number;
  currencyCode: string;
  currencyName: string;
  date: number;
  rate: number;
}

export class CurrencyRateRepository extends BaseSqliteRepository implements ICurrencyRateRepository {
  private readonly BASE_FROM = `
    FROM exchange_rates r
    INNER JOIN currencies c ON r.currency_id = c._id
    INNER JOIN available_currencies cl ON c.available_currency_id = cl._id
    WHERE r.is_deleted = 0
      AND c.is_deleted = 0
      AND c.is_default = 0
  `;

  private readonly BASE_FIELDS = [
    'r._id as id',
    'cl.ticker as currencyCode',
    'c.full_name as currencyName',
    'r.rate_date as date',
    'r.rate as rate',
  ];

  async count(filter?: RateFilter): Promise<number> {
    const sql = `SELECT COUNT(*) AS total ${this.BASE_FROM}`;
    const { query, params } = this.applyFilter(sql, filter);
    const result = await this.queryOne<{ total: number }>(query, params);
    return result?.total ?? 0;
  }

  async findAll(cursor: RateCursor, filter?: RateFilter): Promise<CurrencyRate[]> {
    const sql = `
      SELECT
        ${this.BASE_FIELDS.join(',\n ')}
      ${this.BASE_FROM}
    `;

    // eslint-disable-next-line prefer-const
    let { query, params } = this.applyFilter(sql, filter);

    query += ` ORDER BY r.rate_date DESC, c.position ASC LIMIT ? OFFSET ?`;
    params.push(cursor.limit, cursor.offset ?? 0);

    const rows = await this.query<RateRow>(query, params);
    return rows.map((row) => this.mapRowToEntity(row));
  }

  async getById(id: number): Promise<CurrencyRate | null> {
    const query = `
      SELECT
      ${this.BASE_FIELDS.join(',\n ')}
      ${this.BASE_FROM}
       AND r._id = ?`;
    const row = await this.queryOne<RateRow>(query, [id]);
    return row ? this.mapRowToEntity(row) : null;
  }

  async getLatestRate(currencyCode: string, filter?: RateFilter): Promise<number | null> {
    const sql = `
      SELECT
        r.rate as rate
      ${this.BASE_FROM}
        AND cl.ticker = ?
      `;

    // eslint-disable-next-line prefer-const
    let { query, params } = this.applyFilter(sql, filter, [currencyCode]);
    query += `
      ORDER BY r.rate_date DESC, r._id DESC
      LIMIT 1
    `;
    const row = await this.queryOne<{ rate: number }>(query, params);
    return row ? row.rate : null;
  }

  async getMostRecentRate(): Promise<CurrencyRate | null> {
    const sql = `
      SELECT
        ${this.BASE_FIELDS.join(',\n ')}
      ${this.BASE_FROM}
      ORDER BY r.rate_date DESC, r._id DESC
      LIMIT 1
    `;
    const row = await this.queryOne<RateRow>(sql);
    return row ? this.mapRowToEntity(row) : null;
  }

  async upsert(rate: Omit<CurrencyRate, 'id' | 'currencyName'>): Promise<number> {
    const date = new Date(rate.date).setUTCHours(0, 0, 0, 0) / 1000;

    const result = await this.queryOne<{ id: number }>(
      `INSERT OR REPLACE INTO exchange_rates (rate_date, currency_id, rate)
       VALUES (?, (SELECT c._id FROM currencies c INNER JOIN available_currencies cl ON c.available_currency_id = cl._id WHERE cl.ticker = ? AND c.is_default = 0), ?)
       RETURNING _id AS id`,
      [date, rate.currencyCode, rate.rate],
    );

    if (!result?.id) {
      throw new SqliteQueryError('INSERT INTO currency_rates', [], 'Failed to insert rate');
    }

    return result.id;
  }

  async updateRate(id: number, rate: number): Promise<void> {
    // Update the rate in the exchange_rates table
    await this.execute(
      `
        UPDATE exchange_rates
        SET rate = ?,
            updated_at = strftime('%s','now')
        WHERE _id = ?;
      `,
      [rate, id],
    );

    // Update corresponding expenses with the same date and currency
    await this.execute(
      `
        UPDATE expenses
        SET exchange_rate = ?,
            updated_at = strftime('%s','now')
        FROM exchange_rates r
        WHERE expenses.expense_date = r.rate_date
          AND expenses.currency_id = r.currency_id
          AND r._id = ?
          AND expenses.is_deleted = 0;
      `,
      [rate, id],
    );

    // Update corresponding incomes with the same date and currency
    await this.execute(
      `
        UPDATE incomes
        SET exchange_rate = ?,
            updated_at = strftime('%s','now')
        FROM exchange_rates r
        WHERE incomes.income_date = r.rate_date
          AND incomes.currency_id = r.currency_id
          AND r._id = ?
          AND incomes.is_deleted = 0;
      `,
      [rate, id],
    );
  }

  async delete(id: number): Promise<void> {
    await this.execute('DELETE FROM exchange_rates WHERE _id = ?', [id]);
  }

  private applyFilter(
    query: string,
    filter?: RateFilter,
    params: unknown[] = [],
  ): { query: string; params: unknown[] } {
    if (filter?.dateFrom) {
      query += ` AND r.rate_date >= ?`;
      params.push(new Date(filter.dateFrom).setUTCHours(0, 0, 0, 0) / 1000);
    }

    if (filter?.dateTo) {
      query += ` AND r.rate_date < ?`;
      params.push(new Date(filter.dateTo).setUTCHours(0, 0, 0, 0) / 1000);
    }

    if (filter?.showHiddenCurrencies !== true) {
      query += ` AND c.is_hidden = 0`;
    }

    return { query, params };
  }

  private mapRowToEntity(row: RateRow): CurrencyRate {
    const model = {
      id: row.id,
      currencyCode: row.currencyCode,
      currencyName: row.currencyName,
      date: new Date(row.date * 1000),
      rate: row.rate,
    };

    return model;
  }
}
